<script setup>
  import { ref, computed, onMounted } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useWindAlertStore } from '@/stores/WindAlertStore';
  import { useApiWindAlerts } from '@/composables/useApiWindAlerts';
  import { useToaster } from '@/composables/useToaster';
  import { useGoogleAnalyticsStore } from '@/stores/GoogleAnalyticsStore';
  import { escapeHtml } from '@/helpers/escapeHtml';
  import AlertDeviceSlot from './AlertDeviceSlot.vue';
  import ModalAlertDevicesManageDetailViewEmail from './ModalAlertDevicesManageDetailViewEmail.vue';
  import ModalAlertDevicesManageDetailViewMobile from './ModalAlertDevicesManageDetailViewMobile.vue';
  import ModalAlertDevicesManageDetailViewIphone from './ModalAlertDevicesManageDetailViewIphone.vue';
  import ModalAlertDevicesManageDetailViewAndroid from './ModalAlertDevicesManageDetailViewAndroid.vue';
  import ModalDeleteAlertDevice from './ModalDeleteAlertDevice.vue';

  const emits = defineEmits(['saved']);

  const windAlertStore = useWindAlertStore();
  const apiWindAlerts = useApiWindAlerts();
  const googleAnalyticsStore = useGoogleAnalyticsStore();
  const toaster = useToaster();

  const { t } = useI18n();

  const isLoading = ref(false);
  const editingDevice = ref(null);
  const editingType = ref(null);
  const activeView = ref(null);
  const showDeleteModal = ref(false);
  const detailRef = ref(null);

  const websiteTypes = ['E-Mail']; // 'Mobile' (text messages) is not supported at this time.
  const appTypes = ['iPhone', 'Android'];

  const isSetupMode = computed(() => activeView.value !== null && editingDevice.value === null);
  const isEditMode = computed(() => activeView.value !== null && editingDevice.value !== null);
  const isAtRoot = computed(() => activeView.value === null);

  const canDelete = computed(() =>
    isEditMode.value && (editingType.value === 'E-Mail' || editingType.value === 'Mobile')
  );

  const deleteLabel = computed(() => {
    if (!editingDevice.value) return '';
    return displayType(editingDevice.value.type);
  });

  const title = computed(() => {
    if (isAtRoot.value) return t('alertDevices.devices');
    if (!editingType.value) return t('alertDevices.devices');
    return `${isSetupMode.value ? t('alertDevices.new') : t('common.edit')} ${displayType(editingType.value)} Device`;
  });

  const submitLabel = computed(() => {
    if (isAtRoot.value) return '';
    return isSetupMode.value ? t('alertDevices.createDevice') : t('common.save');
  });

  const errorCount = computed(() => detailRef.value?.errorCount ?? 0);
  const canSubmit = computed(() => !isAtRoot.value);

  const websiteSlots = computed(() => websiteTypes.map(type => ({ type, device: deviceForType(type) })));
  const appSlots = computed(() => appTypes.map(type => ({ type, device: deviceForType(type) })));

  function parseEmailList(value) {
    if (Array.isArray(value)) {
      return value.map(a => String(a).trim()).filter(Boolean);
    }
    return String(value || '').split(',').map(a => a.trim()).filter(Boolean);
  }

  function displayType(type) {
    return windAlertStore.deviceTypeMap[type]?.typeDisplay || type;
  }

  function deviceForType(type) {
    return (windAlertStore.alertDevices || []).find(d => d.type === type) || null;
  }

  function formatPhone(raw) {
    const digits = String(raw || '').replace(/\D/g, '').slice(-10);
    if (digits.length !== 10) return raw;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  function deviceSubText(device) {
    if (device.type === 'E-Mail') {
      const addresses = parseEmailList(device.address);
      const count = addresses.length;
      if (count === 1) return addresses[0];
      return t('windAlerts.emailAddressCount', { count });
    }
    if (device.type === 'Mobile') {
      const phoneRaw = (device.address || '').split('@')[0];
      return `${device.name || ''} - ${formatPhone(phoneRaw)}`;
    }
    if (device.name && device.name !== device.type) return device.name;
    return '';
  }

  function back() {
    activeView.value = null;
    editingDevice.value = null;
    editingType.value = null;
  }

  async function loadDevices(overrideCache = false) {
    isLoading.value = true;
    await windAlertStore.getAlertDevices(undefined, overrideCache);
    isLoading.value = false;
  }

  async function setDeviceEnabled(device, value) {
    device.enabled = value;

    googleAnalyticsStore.sendEvent(value ? 'Wind_Alert_Device_On' : 'Wind_Alert_Device_Off');

    await apiWindAlerts.changeAlertStatusForDevice({ alert_device_id: device.id, enabled: value });
    await loadDevices(true);
  }

  function openEditView(device) {
    editingDevice.value = device;
    editingType.value = device.type;
    activeView.value = windAlertStore.deviceTypeMap[device.type]?.viewKey || 'email';
  }

  function openSetupView(type) {
    editingDevice.value = null;
    editingType.value = type;
    activeView.value = windAlertStore.deviceTypeMap[type]?.viewKey;
  }

  function openDeleteModal() {
    showDeleteModal.value = true;
  }

  async function handleDeleteConfirm() {
    if (!editingDevice.value) return;
    await apiWindAlerts.deleteAlertDevice({ device_id: editingDevice.value.id });
    showDeleteModal.value = false;
    await loadDevices(true);
    back();
  }

  async function submit() {
    if (isAtRoot.value) return true;
    if (!detailRef.value?.validate()) return false;

    const payload = {
      ...detailRef.value.buildPayload(),
      type_id: windAlertStore.deviceTypeMap[editingType.value]?.id,
    };

    const wasEdit = isEditMode.value;
    const deviceTypeLabel = displayType(editingType.value);

    if (wasEdit) {
      await apiWindAlerts.updateAlertDevice({ device_id: editingDevice.value.id, ...payload });
    } else {
      await apiWindAlerts.addAlertDevice(payload);
    }

    await loadDevices(true);

    toaster.toast({
      type: 'success',
      contentHtml: `${wasEdit ? 'Saved' : 'Created'} <strong>${escapeHtml(deviceTypeLabel)}</strong> device`,
    });

    emits('saved');
    back();
    return true;
  }

  onMounted(() => {
    loadDevices();
  });

  defineExpose({
    title,
    submitLabel,
    errorCount,
    canSubmit,
    isAtRoot,
    back,
    submit,
  });
</script>

<template>
  <div>
    <div v-if="isAtRoot">
      <div class="jwx-modal__section">
        <div class="jwx-modal__section-body">
          <p class="jwx-modal__section-description">
            {{ t('alertDevices.manageDescription') }}
          </p>
        </div>
      </div>

      <div class="jwx-modal__section">
        <div class="jwx-modal__section-header">
          <h3 class="jwx-modal__section-title">{{ t('alertDevices.websiteDevices') }}</h3>
        </div>
        <div class="jwx-modal__section-body">
          <ul class="jwx-block jwx-table-view" data-surface="1">
            <AlertDeviceSlot
              v-for="slot in websiteSlots"
              :key="slot.type"
              :type="slot.type"
              :device="slot.device"
              kind="website"
              :subText="slot.device ? deviceSubText(slot.device) : ''"
              @edit="openEditView"
              @setup="openSetupView"
              @toggle="setDeviceEnabled"
            />
          </ul>
        </div>
      </div>

      <div class="jwx-modal__section">
        <div class="jwx-modal__section-header">
          <h3 class="jwx-modal__section-title">{{ t('alertDevices.appDevices') }}</h3>
        </div>
        <div class="jwx-modal__section-body">
          <ul class="jwx-block jwx-table-view" data-surface="1">
            <AlertDeviceSlot
              v-for="slot in appSlots"
              :key="slot.type"
              :type="slot.type"
              :device="slot.device"
              kind="app"
              :subText="slot.device ? deviceSubText(slot.device) : ''"
              @edit="openEditView"
              @toggle="setDeviceEnabled"
            />
          </ul>
        </div>
      </div>
    </div>

    <ModalAlertDevicesManageDetailViewEmail
      v-else-if="activeView === 'email'"
      ref="detailRef"
      :device="editingDevice"
      :canDelete="canDelete"
      @delete="openDeleteModal"
    />
    <ModalAlertDevicesManageDetailViewMobile
      v-else-if="activeView === 'mobile'"
      ref="detailRef"
      :device="editingDevice"
      :canDelete="canDelete"
      @delete="openDeleteModal"
    />
    <ModalAlertDevicesManageDetailViewIphone
      v-else-if="activeView === 'iphone'"
      ref="detailRef"
      :device="editingDevice"
    />
    <ModalAlertDevicesManageDetailViewAndroid
      v-else-if="activeView === 'android'"
      ref="detailRef"
      :device="editingDevice"
    />

    <ModalDeleteAlertDevice
      :show="showDeleteModal"
      :deviceLabel="deleteLabel"
      @close="showDeleteModal = false"
      @confirm="handleDeleteConfirm"
    />
  </div>
</template>
